import React, { useEffect, useMemo, useRef, useState } from 'react';
import { TextField, Grid, Autocomplete } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormProps, getRequiredValidationRule, getValidationErrorProps } from 'util/Form';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import { ApplicationQuestionOption } from 'store/types/ApplicationQuestion';
import ExplanationQuestionFormItem from '../ExplanationQuestionFormItem';

interface DropdownCustomListQuestionFormItemProps extends FormProps {
  fieldName: string;
  options?: ApplicationQuestionOption[];
}

const DropdownCustomListQuestionFormItem: React.FunctionComponent<DropdownCustomListQuestionFormItemProps> = ({
  fieldName,
  options = [],
  required,
  disabled,
}) => {
  const { control, setValue, getValues } = useFormContext();
  const fieldValue = useWatch({ control, name: fieldName });
  const [selectedValue, setSelectedValue] = useState<ApplicationQuestionOption | null>(null);
  const hasHydratedInitialValue = useRef(false);
  const optionExplanation: ApplicationQuestionOption | undefined = useMemo(
    () =>
      selectedValue
        ? options.find(({ includeExplanation, value }) => !!includeExplanation && value === selectedValue.value)
        : undefined,
    [options, selectedValue]
  );

  useEffect(() => {
    if (hasHydratedInitialValue.current) {
      return;
    }

    if (!fieldValue) {
      hasHydratedInitialValue.current = true;
      return;
    }

    const selectedOption = options.find(({ name }) => name === fieldValue);

    if (selectedOption) {
      setSelectedValue(selectedOption);
    }

    hasHydratedInitialValue.current = true;
  }, [options, setValue, fieldName, getValues, fieldValue]);

  return (
    <Grid {...defaultGridContainerProps}>
      <Grid {...defaultGridItemProps}>
        <Controller
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              blurOnSelect
              autoHighlight
              value={selectedValue}
              disabled={disabled}
              options={options}
              getOptionLabel={(option) => option.name || ''}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              onChange={(_event, data) => {
                setSelectedValue(data);
                field.onChange(data?.name);
              }}
              renderOption={(props, option) => (
                <li {...props} key={`${fieldName}-option-${option.value}`}>
                  {option.name ?? ''}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} {...getValidationErrorProps(error)} inputRef={field.ref} required={required} />
              )}
            />
          )}
          name={fieldName}
          control={control}
          rules={{
            required: getRequiredValidationRule('your answer', true, required),
          }}
          defaultValue={''}
        />
      </Grid>
      {optionExplanation && (
        <Grid {...defaultGridItemProps}>
          <ExplanationQuestionFormItem
            parentFieldName={fieldName}
            required={required}
            defaultValue={optionExplanation.explanation}
            disabled={disabled}
          />
        </Grid>
      )}
    </Grid>
  );
};
export default DropdownCustomListQuestionFormItem;
